import { useEffect } from 'react';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

const steps = [
  { icon: 'ri-search-eye-line', title: 'Mold Inspection', desc: 'We conduct a thorough inspection using moisture meters and air quality testing to locate all mold colonies.' },
  { icon: 'ri-shield-line', title: 'Containment', desc: 'Affected areas are sealed off with negative air pressure barriers to prevent spore spread during removal.' },
  { icon: 'ri-wind-line', title: 'Air Filtration', desc: 'HEPA air scrubbers run continuously to capture airborne mold spores and improve indoor air quality.' },
  { icon: 'ri-delete-bin-line', title: 'Mold Removal', desc: 'All mold-infested materials are safely removed and disposed of following EPA and IICRC guidelines.' },
  { icon: 'ri-drop-line', title: 'Cleaning & Treatment', desc: 'Surfaces are cleaned with antimicrobial agents to eliminate remaining spores and prevent regrowth.' },
  { icon: 'ri-home-smile-line', title: 'Restoration', desc: 'We repair and restore affected areas, including drywall, insulation, and flooring, to like-new condition.' },
];

const reasons = [
  { icon: 'ri-award-line', title: 'Certified Mold Specialists', desc: 'Our technicians are IICRC certified in mold remediation and follow strict EPA protocols.' },
  { icon: 'ri-shield-check-line', title: 'Safe & Thorough Removal', desc: 'We don\'t just clean the surface — we eliminate mold at the source and prevent it from coming back.' },
  { icon: 'ri-file-list-3-line', title: 'Insurance Assistance', desc: 'We document everything and work with your insurance company to support your claim.' },
  { icon: 'ri-tools-line', title: 'HEPA Equipment', desc: 'Industrial HEPA air scrubbers and negative air machines ensure complete spore containment.' },
  { icon: 'ri-map-pin-line', title: 'Serving Merced County', desc: 'Local experts who understand the humidity and climate conditions that drive mold growth in the Central Valley.' },
  { icon: 'ri-thumb-up-line', title: 'Post-Remediation Testing', desc: 'We verify successful remediation with post-treatment air quality testing for your peace of mind.' },
];

export default function MoldRemediationPage() {
  useEffect(() => {
    const siteUrl = 'https://mercedwaterrestoration.com';

    document.title = 'Mold Remediation Merced CA | EPA-Certified Mold Removal | Indoor-Restore';

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', 'EPA-compliant mold inspection, containment, removal & prevention in Merced, CA. IICRC certified specialists with HEPA equipment and post-remediation testing. Call (209) 451-2288.');

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', 'mold remediation Merced CA, mold removal Central Valley, black mold removal Merced, mold inspection Merced County, mold cleanup California');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${siteUrl}/services/mold-remediation`);

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Mold Remediation",
      "name": "Mold Remediation in Merced, CA",
      "description": "EPA-compliant mold inspection, containment, removal, and prevention services in Merced County. IICRC certified mold specialists with HEPA equipment and post-remediation testing.",
      "url": `${siteUrl}/services/mold-remediation`,
      "provider": {
        "@type": "LocalBusiness",
        "name": "Indoor-Restore Services",
        "telephone": "+12094512288",
        "email": "info@mercedwaterrestoration.com",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "1729 U St",
          "addressLocality": "Merced",
          "addressRegion": "CA",
          "postalCode": "95340",
          "addressCountry": "US"
        }
      },
      "areaServed": {
        "@type": "City",
        "name": "Merced",
        "containedInPlace": { "@type": "State", "name": "California" }
      },
      "availableChannel": {
        "@type": "ServiceChannel",
        "servicePhone": {
          "@type": "ContactPoint",
          "telephone": "+12094512288",
          "contactType": "Customer Service",
          "availableLanguage": "English"
        }
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/de61759dc614aa976f59660a66393f16.jpg"
            alt="Mold Remediation Merced CA"
            className="w-full h-full object-cover object-top"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/55 to-black/65"></div>
        </div>
        <div className="relative z-10 text-center px-4 w-full max-w-5xl mx-auto py-24">
          <span className="inline-block bg-green-500/30 border border-green-400/50 text-green-200 text-sm font-semibold px-4 py-1.5 rounded-full mb-5">
            EPA-Compliant Remediation
          </span>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-5 leading-tight">
            Mold Remediation<br />in Merced, CA
          </h1>
          <p className="text-xl text-white/85 mb-8 max-w-2xl mx-auto">
            Safe, certified mold removal that protects your family's health and your property's structural integrity.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:2094512288" className="bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-teal-700 transition-all shadow-lg whitespace-nowrap cursor-pointer">
              <i className="ri-phone-line mr-2"></i>Call (209) 451-2288
            </a>
            <a href="/#contact" className="bg-white text-teal-700 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all shadow-lg whitespace-nowrap cursor-pointer">
              Get Free Estimate
            </a>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-5">Professional Mold Removal You Can Trust</h2>
              <p className="text-gray-600 leading-relaxed mb-5">
                Mold is more than an eyesore — it's a serious health hazard that can cause respiratory issues, allergic reactions, and long-term structural damage. In Merced's warm, humid climate, mold can spread rapidly if left untreated.
              </p>
              <p className="text-gray-600 leading-relaxed mb-5">
                <strong>Indoor-Restore Services</strong> uses EPA-approved methods and HEPA-grade equipment to safely contain and eliminate mold from your home or business, then restore the affected areas to a clean, healthy condition.
              </p>
              <ul className="space-y-3">
                {[
                  'Black mold (Stachybotrys) removal',
                  'Bathroom & kitchen mold cleanup',
                  'Basement & crawl space remediation',
                  'HVAC & ductwork mold treatment',
                  'Post-water-damage mold prevention',
                  'Air quality testing & clearance reports',
                ].map(item => (
                  <li key={item} className="flex items-center gap-3 text-gray-700 text-sm">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-teal-500 text-lg"></i>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl h-96">
              <img
                src="https://readdy.ai/api/search-image?query=close%20up%20of%20mold%20remediation%20specialist%20applying%20antimicrobial%20treatment%20to%20mold%20covered%20drywall%20in%20a%20residential%20home%20with%20containment%20plastic%20sheeting%20and%20professional%20equipment%20visible%2C%20clean%20bright%20lighting&width=800&height=600&seq=mr-overview-02&orientation=landscape"
                alt="Mold removal and treatment process"
                className="w-full h-full object-cover object-top"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Mold Remediation Process</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">A safe, systematic process that eliminates mold completely and prevents it from returning.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {steps.map((step, i) => (
              <div key={i} className="bg-white rounded-xl p-7 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${step.icon} text-2xl text-teal-600`}></i>
                  </div>
                  <span className="text-3xl font-bold text-teal-100">{String(i + 1).padStart(2, '0')}</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-white mb-4">Why Choose Indoor-Restore Services?</h2>
            <p className="text-white/80 max-w-2xl mx-auto">Certified mold remediation experts serving Merced, CA and the entire Central Valley.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {reasons.map((r, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-7 border border-white/20 hover:bg-white/20 transition-all">
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center mb-5">
                  <i className={`${r.icon} text-2xl text-teal-600`}></i>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{r.title}</h3>
                <p className="text-white/75 text-sm leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Spotted Mold in Your Home or Business?</h2>
          <p className="text-gray-500 mb-8 text-lg">Don't ignore it — mold spreads fast. Call Indoor-Restore Services for a professional inspection and same-day response in Merced, CA.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:2094512288" className="bg-teal-600 text-white px-10 py-4 rounded-lg text-lg font-bold hover:bg-teal-700 transition-all shadow-lg whitespace-nowrap cursor-pointer">
              <i className="ri-phone-line mr-2"></i>(209) 451-2288
            </a>
            <a href="mailto:info@mercedwaterrestoration.com" className="border-2 border-teal-600 text-teal-600 px-10 py-4 rounded-lg text-lg font-bold hover:bg-teal-50 transition-all whitespace-nowrap cursor-pointer">
              Email Us
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
